import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, CheckCircle2, ExternalLink } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { DEPARTMENT_OPTIONS } from "@/lib/data";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fadeUp, viewport } from "@/lib/motion";

const empty = { full_name: "", phone: "", email: "", high_school: "", gpa: "", department: "" };

export default function ApplicationForm() {
  const [form, setForm] = useState(empty);
  const [googleFormUrl, setGoogleFormUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let active = true;
    api.get("/config")
      .then(({ data }) => { if (active) setGoogleFormUrl(data?.google_form_url || ""); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.phone.trim() || !form.department) {
      toast.error("Lütfen Ad Soyad, Telefon ve Bölüm alanlarını doldur.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      const { data } = await api.post("/applications", payload);
      setDone(true);
      toast.success(data.message || "Başvurun alındı!");
      setForm(empty);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gönderilemedi, tekrar deneyin.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="basvuru" data-testid="application-section" className="py-24 md:py-32 bg-[#1B19A8] relative overflow-hidden">
      <div className="pointer-events-none absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-white/5 blur-3xl" />
      <div className="max-w-6xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 items-center relative">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport}>
          <span className="text-sm uppercase tracking-[0.2em] text-[#FFE500] font-semibold">Ön Başvuru</span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mt-4 leading-tight">
            Hemen Ön Başvurunu Yap
          </h2>
          <p className="mt-5 text-lg text-white/70 leading-relaxed max-w-md">
            Formu doldur, uzman ekibimiz en kısa sürede seninle iletişime geçsin.
            Ücretsiz ve bağlayıcı değildir.
          </p>
          {googleFormUrl && (
            <a
              href={googleFormUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-form-link"
              className="mt-8 inline-flex items-center gap-2 text-white/80 hover:text-white font-medium transition-colors"
            >
              Alternatif olarak Google Form ile başvur <ExternalLink size={16} />
            </a>
          )}
        </motion.div>

        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport} custom={1}>
          {done ? (
            <div data-testid="application-success" className="rounded-3xl bg-white p-10 text-center shadow-[0_30px_80px_rgba(0,0,0,0.2)]">
              <CheckCircle2 size={56} className="mx-auto text-[#1B19A8] mb-4" />
              <h3 className="font-display text-2xl font-bold text-[#1a1a1a] mb-2">Başvurun alındı!</h3>
              <p className="text-[#1a1a1a]/65">En kısa sürede seninle iletişime geçeceğiz.</p>
              <button
                onClick={() => setDone(false)}
                className="mt-6 rounded-full bg-[#f7f9fa] px-6 py-3 font-semibold text-[#1B19A8] hover:bg-[#eef2f3] transition-colors"
              >
                Yeni Başvuru
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="application-form"
              className="rounded-3xl bg-white p-7 sm:p-9 shadow-[0_30px_80px_rgba(0,0,0,0.2)] space-y-4"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="full_name" className="text-[#1a1a1a] mb-1.5 block">Ad Soyad *</Label>
                  <Input id="full_name" data-testid="input-full-name" value={form.full_name} onChange={set("full_name")} placeholder="Ad Soyad" required />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-[#1a1a1a] mb-1.5 block">Telefon / WhatsApp *</Label>
                  <Input id="phone" data-testid="input-phone" value={form.phone} onChange={set("phone")} placeholder="+90 5xx xxx xx xx" required />
                </div>
              </div>
              <div>
                <Label htmlFor="email" className="text-[#1a1a1a] mb-1.5 block">E-posta</Label>
                <Input id="email" type="email" data-testid="input-email" value={form.email} onChange={set("email")} placeholder="ornek@eposta.com" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="high_school" className="text-[#1a1a1a] mb-1.5 block">Mezun olduğun lise</Label>
                  <Input id="high_school" data-testid="input-highschool" value={form.high_school} onChange={set("high_school")} placeholder="Lise adı" />
                </div>
                <div>
                  <Label htmlFor="gpa" className="text-[#1a1a1a] mb-1.5 block">Diploma ortalaması</Label>
                  <Input id="gpa" data-testid="input-gpa" value={form.gpa} onChange={set("gpa")} placeholder="0–100 veya 4'lük" />
                </div>
              </div>
              <div>
                <Label className="text-[#1a1a1a] mb-1.5 block">İlgilendiğin bölüm *</Label>
                <Select value={form.department} onValueChange={(v) => setForm((f) => ({ ...f, department: v }))}>
                  <SelectTrigger data-testid="select-department" className="w-full">
                    <SelectValue placeholder="Bölüm seç" />
                  </SelectTrigger>
                  <SelectContent>
                    {DEPARTMENT_OPTIONS.map((o) => (
                      <SelectItem key={o} value={o} data-testid={`dept-option-${o}`}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="submit-application"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#FFE500] px-7 py-4 text-base font-bold text-[#1a1a1a] hover:bg-[#EACE00] active:scale-[0.98] transition-[background-color,transform] disabled:opacity-60"
              >
                {submitting ? "Gönderiliyor..." : "Başvur"}
                {!submitting && <Send size={18} />}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
